import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { useSalesUnits, type SalesUnit } from "@/hooks/use-sales-units";
import { SrIcon } from "@/components/ui/SrIcon";

interface SelectedSalesUnitInput {
  salesUnitId: string;
  ownershipPct: number;
}

interface StepProjectOwnersProps {
  selectedSalesUnits: SelectedSalesUnitInput[];
  onToggleSalesUnit: (unit: { id: string; members: { user_id: string }[] }) => void;
  /** Året salgsenhetene gjelder for. Default: inneværende år. */
  year?: number;
}

function memberInitials(name: string): string {
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function MemberAvatar({ name, src }: { name: string; src?: string | null }) {
  if (src) {
    return <img src={src} className="w-6 h-6 rounded-full object-cover ring-2 ring-background" alt={name} />;
  }
  return (
    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-medium flex items-center justify-center ring-2 ring-background">
      {memberInitials(name)}
    </span>
  );
}

/**
 * VIB-683: andelen utledes automatisk fra antall valgte salgsenheter
 * (100 / N med hele tall — en enhet kan få +1 for at summen blir 100).
 * Brukeren huker bare av, ingen manuelle %-input.
 */
export function StepProjectOwners({
  selectedSalesUnits,
  onToggleSalesUnit,
  year,
}: StepProjectOwnersProps) {
  const currentYear = year ?? new Date().getFullYear();
  const { data: units, isLoading } = useSalesUnits(currentYear);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground">
        <SrIcon name="loader2" className="w-4 h-4 animate-spin mr-2" />
        Henter selgere…
      </div>
    );
  }

  if (!units || units.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-6 border rounded-lg">
        Ingen selgere er konfigurert for {currentYear}. Sett dem opp i admin før du oppretter prosjekter.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {units.map((u: SalesUnit) => {
          const selected = selectedSalesUnits.find(s => s.salesUnitId === u.id);
          const members = u.members ?? [];
          return (
            <div key={u.id} className="flex items-center gap-3 p-3 rounded-lg border">
              <Checkbox
                checked={!!selected}
                onCheckedChange={() =>
                  onToggleSalesUnit({
                    id: u.id,
                    members: members.map(m => ({ user_id: m.user_id })),
                  })
                }
                aria-label={`Velg ${u.name}`}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{u.name}</div>
                {members.length > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <div className="flex -space-x-1.5">
                      {members.map(m => (
                        <MemberAvatar
                          key={m.user_id}
                          name={m.user?.full_name ?? "?"}
                          src={m.user?.avatar_url}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground ml-2">
                      {members.length} medlem{members.length === 1 ? "" : "mer"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
